import React, { useEffect, useState } from "react";
import { getAccessMatrix } from "../services/accessService";
import type { MatrixRow } from "../types/access";
import { WIDGET_NAME_MAP } from "../utils/maps";
import QuickLinksConfig from "./QuickLinksConfig";
import DashboardConfig from "./DashboardConfig";
import TimezoneConfig from "./TimezoneConfig";

const WidgetConfiguration: React.FC = () => {
  const [widgets, setWidgets] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [expandedWidget, setExpandedWidget] = useState<string | null>(null);

  useEffect(() => {
    loadWidgets();
  }, []);

  const loadWidgets = async () => {
    setLoading(true);
    try {
      const data = await getAccessMatrix();

      const list = data.matrix.map(
        (row: MatrixRow) =>
          WIDGET_NAME_MAP[row.feature_code] || row.feature_code
      );

      const filteredList = list.filter(
        (w: string) =>
          !["Team Calendar", "Learning Insights", "Settings","Compass"].includes(w)
      );

      filteredList.push("Timezone Bar");

      const uniqueWidgets = Array.from(new Set(filteredList));

      setWidgets(uniqueWidgets);
    } catch (e) {
      console.error("Failed to load widgets");
    } finally {
      setLoading(false);
    }
  };

  const filtered = widgets.filter((w) =>
    w.toLowerCase().includes(search.toLowerCase())
  );

  const isEditable = (widget: string) =>
    widget === "Quick Links" ||
    widget === "Dashboard" ||
    widget === "Timezone Bar";

  if (loading) return <div className="p-5">Loading...</div>;

  return (
    <div className="bg-[#f6f4ef] min-h-screen font-sans">
      <div className="p-5">

        <div className="bg-white border border-gray-200 rounded-md">

          <div className="px-5 py-4 border-b border-gray-200 flex items-center">
            <div className="flex-1">
              <h2 className="text-md font-semibold text-gray-800">
                Widget Configuration
              </h2>
            </div>
          </div>

          <div className="p-5">

            <input
              type="text"
              placeholder="Search widgets..."
              className="w-full border border-gray-200 p-2 rounded mb-4 focus:outline-none focus:ring-2 focus:ring-orange-400"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <div className="space-y-3 max-h-[600px] overflow-auto">

              {filtered.map((widget, index) => {
                const isExpanded = expandedWidget === widget;
                const editable = isEditable(widget);

                return (
                  <div key={index} className="border border-gray-200 rounded-md bg-white">

                    <div className="p-4 flex justify-between items-center">
                      <div className="text-sm font-medium text-gray-800">
                        {widget}
                      </div>

                      <button
                        className={`px-3 py-1.5 rounded-md text-sm ${
                          editable
                            ? "bg-orange-500 text-white"
                            : "bg-gray-200 text-gray-500 cursor-not-allowed"
                        }`}
                        onClick={() =>
                          editable && setExpandedWidget(isExpanded ? null : widget)
                        }
                        disabled={!editable}
                      >
                        {isExpanded ? "Close" : "Edit"}
                      </button>
                    </div>

                    {isExpanded && widget === "Quick Links" && (
                      <div className="border-t border-gray-200 p-4 bg-[#fbfaf7]">
                        <QuickLinksConfig widget={widget} onBack={() => {}} />
                      </div>
                    )}

                    {isExpanded && widget === "Dashboard" && (
                      <div className="border-t border-gray-200 p-4 bg-[#fbfaf7]">
                        <DashboardConfig />
                      </div>
                    )}

                    {isExpanded && widget === "Timezone Bar" && (
                      <div className="border-t border-gray-200 p-4 bg-[#fbfaf7]">
                        <TimezoneConfig />
                      </div>
                    )}

                  </div>
                );
              })}

              {filtered.length === 0 && (
                <div className="text-sm text-gray-500 text-center py-6">
                  No widgets found
                </div>
              )}

            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

export default WidgetConfiguration;